import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Header from "./Header";
import Footer from "./Footer";
import { toast } from "react-toastify";

import {
  fetchProductsWithFilter,
  selectProducts,
  selectProductsStatus,
  selectProductsError,
  selectProductsMeta,
  selectProductsFilters,
  setProductsFilters,
  setProductsPage,
} from "../redux/slices/productsSlice";

import {
  addToCartOrWishlist,
  selectUpdatingByProductId,
  selectCartError,
} from "../redux/slices/cartSlice";

function Products() {
  const dispatch = useDispatch();
  const products = useSelector(selectProducts);
  const status = useSelector(selectProductsStatus);
  const errorMsg = useSelector(selectProductsError);
  const meta = useSelector(selectProductsMeta);
  const filters = useSelector(selectProductsFilters);
  const addingByProductId = useSelector(selectUpdatingByProductId);
  const cartError = useSelector(selectCartError);
  const [allCategories, setAllCategories] = useState([]);

  useEffect(() => {
    if (!filters.category && products?.length) {
      setAllCategories([
        ...new Set(products.map((p) => p.category).filter(Boolean)),
      ]);
    }
  }, [products, filters.category]);

  const categoriesUI = useMemo(
    () => ["All Products", ...allCategories],
    [allCategories],
  );

  const loading = status === "loading";

  /* ── local search input with 400ms debounce → dispatches to Redux ── */
  const [searchInput, setSearchInput] = useState(filters.search || "");

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchInput.trim() || null;
      // only dispatch if value actually changed
      if (trimmed !== (filters.search || null)) {
        dispatch(setProductsFilters({ search: trimmed }));
        dispatch(setProductsPage(1));
      }
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const requestParams = useMemo(
    () => ({
      category: filters.category,
      inStock: filters.inStock,
      search: filters.search,
      page: meta.page,
      limit: meta.limit,
    }),
    [filters.category, filters.inStock, filters.search, meta.page, meta.limit],
  );

  useEffect(() => {
    dispatch(fetchProductsWithFilter(requestParams));
  }, [dispatch, requestParams]);

  /* ── helpers ── */
  const isSelectedCategory = (category) => {
    if (category === "All Products") {
      return !filters.category;
    }

    return filters.category === category;
  };

  const onPickCategory = (category) => {
    dispatch(
      setProductsFilters({
        category: category === "All Products" ? null : category,
      }),
    );

    dispatch(setProductsPage(1));
  };

  const goPrev = () => dispatch(setProductsPage(Math.max(1, meta.page - 1)));
  const goNext = () =>
    dispatch(setProductsPage(Math.min(meta.totalPages, meta.page + 1)));

  const handleAddToCart = async (productId) => {
    try {
      await dispatch(
        addToCartOrWishlist({ type: "cart", productId, quantity: 1 }),
      ).unwrap();
      toast.success("Added to cart");
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to add to cart");
    }
  };

  const handleAddToWishlist = async (productId) => {
    try {
      await dispatch(
        addToCartOrWishlist({ type: "wishlist", productId }),
      ).unwrap();
      toast.success("Added to wishlist");
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to add to wishlist");
    }
  };

  const getImage0 = (p) =>
    Array.isArray(p?.images) && p.images.length > 0 ? p.images[0] : null;

  return (
    <>
      <Header />

      <main>
        <section className="pb-2 bg-white">
          <div className="container">
            <div className="row pt-4">
              <div className="d-flex justify-content-between mb-2">
                <div className="w-100">
                  {/* ── Section heading ── */}
                  <div className="d-flex justify-content-between">
                    <div>
                      <span className="text-uppercase text-secondary fw-semibold fs-6 ls-md">
                        OUR PRODUCTS
                      </span>
                      <h2 className="fw-semibold ls-sm text-success fs-3 lh-1">
                        Product Categories We Offer
                      </h2>
                      <div className="mt-n3 pt-2">
                        <img
                          src="/assets/images/line-shap.webp"
                          width="150"
                          alt=""
                        />
                      </div>
                    </div>

                    {/* ── Search filter input ── */}
                    <div className="mt-3" style={{ width: 400 }}>
                      <div className="input-group">
                        <span className="input-group-text bg-light border-0 rounded-start-pill ps-4">
                          {loading && searchInput ? (
                            <span className="spinner-border spinner-border-sm text-secondary" />
                          ) : (
                            <i className="bi bi-search text-secondary" />
                          )}
                        </span>
                        <input
                          type="search"
                          className="form-control border-0 bg-light rounded-end-pill ps-0 outline-0 form-focus-none"
                          placeholder="Search Products"
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                          style={{ fontSize: 15 }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* ── Category pills ── */}
                  <div className="mt-3 d-flex flex-wrap align-items-center">
                    {categoriesUI.map((category) => (
                      <div
                        key={category}
                        className={`rounded-3 py-1 px-4 me-1 mb-1 d-inline-block cursor-pointer ${
                          isSelectedCategory(category) ? "bg-dark" : "bg-light"
                        }`}
                        role="button"
                        onClick={() => onPickCategory(category)}
                      >
                        <span
                          className={`font-semibold h6 fw-medium text-capitalize ${
                            isSelectedCategory(category)
                              ? "text-white"
                              : "text-gray-800"
                          }`}
                        >
                          {category}
                        </span>
                      </div>
                    ))}
                  </div>

                  {errorMsg && (
                    <div className="alert alert-danger py-2 mt-3 mb-0">
                      {errorMsg}
                    </div>
                  )}
                  {cartError && (
                    <div className="alert alert-danger py-2 mt-2 mb-0">
                      {cartError}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Loading spinner ── */}
              {loading && (
                <div className="d-flex justify-content-center py-5">
                  <div
                    className="spinner-border"
                    role="status"
                    aria-hidden="true"
                  />
                </div>
              )}

              {/* ── Products grid ── */}
              {!loading && (
                <div className="row g-2 mt-2">
                  {products?.length ? (
                    products.map((p) => {
                      const isAdding = !!addingByProductId[p.productId];
                      return (
                        <div className="col-lg-3 col-6" key={p.productId}>
                          <div className="row g-0 shadow rounded-4 h-100">
                            <div className="col-12">
                              <Link to="/course">
                                <div className="position-relative">
                                  <img
                                    src={getImage0(p)}
                                    alt={p.name}
                                    className="w-100 object-fit-contain rounded-top-4"
                                    height="150"
                                    loading="lazy"
                                  />
                                </div>
                              </Link>
                            </div>

                            <div className="col-12 px-3 border pb-4 pt-1 border-1 rounded-bottom-4 border-top-0">
                              <div className="d-flex flex-column px-2 mb-1">
                                <div className="d-flex flex-column gap-2 mt-3">
                                  <div className="d-flex flex-column">
                                    <h3 className="mb-0 lh-1">
                                      <p className="fs-4 text-dark lh-1 mb-0">
                                        {p.name}
                                      </p>
                                    </h3>
                                    <span className="text-danger mt-2 text-capitalize">
                                      {p.category}
                                    </span>
                                  </div>
                                  <div className="lh-1">
                                    <span className="text-secondary fw-semibold">
                                      4.5
                                    </span>
                                    <span className="align-text-top ms-3 me-2">
                                      <i className="bi bi-star-fill text-warning"></i>
                                      <i className="bi bi-star-fill text-warning"></i>
                                      <i className="bi bi-star-fill text-warning"></i>
                                      <i className="bi bi-star-fill text-warning"></i>
                                      <i className="bi bi-star-fill text-warning"></i>
                                    </span>
                                  </div>
                                </div>

                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <div className="d-flex flex-row gap-2 align-items-center">
                                      <span className="fs-3 lh-1 mt-1 fw-semibold text-dark">
                                        ₹{p.offeredPrice}
                                        <span className="fw-semibold lh-sm text-gray-400 fs-5 ms-2">
                                          <del>₹{p.originalPrice}</del>
                                        </span>
                                      </span>
                                    </div>
                                    <div className="text-success ls-sm fw-semibold">
                                      {p.stock > 0
                                        ? "In Stock"
                                        : "Out of Stock"}
                                    </div>
                                  </div>

                                  <div className="mt-3 d-flex align-items-center">
                                    <button
                                      type="button"
                                      className="btn btn-warning fw-semibold rounded-pill circle-button"
                                      onClick={() =>
                                        handleAddToCart(p.productId)
                                      }
                                      disabled={isAdding || p.stock <= 0}
                                      title="Add to Cart"
                                    >
                                      {isAdding ? (
                                        <span
                                          className="spinner-border spinner-border-sm"
                                          role="status"
                                          aria-hidden="true"
                                        />
                                      ) : (
                                        <i className="bi bi-cart"></i>
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="col-12 py-5 text-center">
                      <h5 className="mb-0 lh-1">No products found</h5>
                      <p className="text-secondary mb-0">
                        Try changing category / filters.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* ── Pagination ── */}
              {!loading && meta.totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-4 pb-2">
                  <div className="text-muted small">
                    Page {meta.page} of {meta.totalPages}
                    {meta.total ? ` · ${meta.total} products` : ""}
                  </div>
                  <nav>
                    <ul className="pagination pagination-sm mb-0 gap-1">
                      <li
                        className={`page-item ${meta.page <= 1 ? "disabled" : ""}`}
                      >
                        <button
                          className="page-link rounded-3"
                          onClick={goPrev}
                        >
                          <i className="bi bi-chevron-left" />
                        </button>
                      </li>

                      {Array.from({ length: meta.totalPages }, (_, i) => i + 1)
                        .filter(
                          (p) =>
                            p === 1 ||
                            p === meta.totalPages ||
                            Math.abs(p - meta.page) <= 2,
                        )
                        .reduce((acc, p, idx, arr) => {
                          if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                          acc.push(p);
                          return acc;
                        }, [])
                        .map((p, i) =>
                          p === "..." ? (
                            <li key={`e-${i}`} className="page-item disabled">
                              <span className="page-link border-0 bg-transparent">
                                …
                              </span>
                            </li>
                          ) : (
                            <li
                              key={p}
                              className={`page-item ${p === meta.page ? "active" : ""}`}
                            >
                              <button
                                className="page-link rounded-3"
                                onClick={() => dispatch(setProductsPage(p))}
                              >
                                {p}
                              </button>
                            </li>
                          ),
                        )}

                      <li
                        className={`page-item ${meta.page >= meta.totalPages ? "disabled" : ""}`}
                      >
                        <button
                          className="page-link rounded-3"
                          onClick={goNext}
                        >
                          <i className="bi bi-chevron-right" />
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}

export default Products;
